import { HttpErrorResponse } from '@angular/common/http';
import {
    ChangeDetectionStrategy,
    Component,
    OnDestroy,
    OnInit,
    ViewEncapsulation,
} from '@angular/core';
import {
    FormBuilder,
    FormGroup,
    Validators,
} from '@angular/forms';
import {
    AppStateFacade,
    Setting,
} from '@app-state';
import { UiModalComponent } from '@components';
import {
    FolderType,
    IFolder,
    ModalResultType,
    RolesAssignment,
} from '@models';
import { FoldersService } from '@services';
import {
    addToArrayIf,
    addToObjectIf,
} from '@utils/object.utils';

import cloneDeep from 'lodash-es/cloneDeep';
import {
    BehaviorSubject,
    of,
    Subject,
} from 'rxjs';
import {
    startWith,
    switchMap,
    takeUntil,
} from 'rxjs/operators';

export interface IUpsertFolderModalData {
    /**
     * Should only be used when intending to add a folder (should be undefined otherwise).
     * Null value means that the new folder will be at root level.
     */
    parent?: IFolder | null;
    current?: IFolder;
}

@Component({
    templateUrl: './upsert-folder.modal.html',
    styleUrls: ['./upsert-folder.modal.scss'],
    providers: [FormBuilder],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UpsertFolderModalComponent
    extends UiModalComponent<IUpsertFolderModalData>
    implements OnInit, OnDestroy {

    public FolderType = FolderType;
    public RolesAssignment = RolesAssignment;

    public form!: FormGroup;
    public loading$ = new BehaviorSubject(false);

    private _foldersService = this._injector.get<FoldersService>(FoldersService);
    private _appState = this._injector.get<AppStateFacade>(AppStateFacade);

    private _destroyed$ = new Subject();

    public get isRoot() {
        return this.context.data.parent === null ||
            !!this.context.data.current && this.context.data.current.parentId === null;
    }

    public get isNewEntry() {
        return !this.context.data.current;
    }

    public get showRolesAssignmentOption() {
        return this.isNewEntry ||
            this.context.data.current!.type === FolderType.Modern;
    }

    ngOnInit() {
        const hasRolesAssignment = !!this.context.data.current && !!this.context.data.current.rolesAssignment;
        const parentId = !!this.context.data.parent && this.context.data.parent.id ||
            !!this.context.data.current && this.context.data.current.parentId;
        const showTypeOption = this.isRoot || hasRolesAssignment;

        const defaultType = this._appState.settings.get(Setting.isModernFolderSystemEnabled) ?
            FolderType.Modern :
            FolderType.Classic;

        const type = this.context.data.current &&
            this.context.data.current.type ||
            defaultType;

        const controls = {
            ...addToObjectIf(
                !!parentId,
                { 'parentId': [parentId] },
            ),

            ...addToObjectIf(
                showTypeOption,
                {
                    'type': [
                        type,
                        ...addToArrayIf(this.isNewEntry, Validators.required),
                    ],
                },
            ),

            ...addToObjectIf(
                this.showRolesAssignmentOption || hasRolesAssignment,
                {
                    'rolesAssignment': [
                        {
                            value: this.context.data.current && this.context.data.current.rolesAssignment ||
                                this.context.data.parent && this.context.data.parent.rolesAssignment ||
                                RolesAssignment.CustomRoles,
                            disabled: (this.context.data.current && this.context.data.current.type === FolderType.Classic) ||
                                !this.isRoot ||
                                !this.isNewEntry,
                        },
                        ...addToArrayIf(this.isNewEntry, Validators.required),
                    ],
                },
            ),

            'displayName': [
                this.context.data.current && this.context.data.current.displayName,
                Validators.compose([
                    Validators.required,
                    Validators.maxLength(115),
                ]),
            ],
            'description': [
                this.context.data.current && this.context.data.current.description,
                Validators.maxLength(500),
            ],
        };

        this.form = this._injector.get<FormBuilder>(FormBuilder).group(controls);

        if (showTypeOption) {
            this.form.get('type')!
                .valueChanges
                .pipe(
                    startWith(type),
                    takeUntil(this._destroyed$),
                )
                .subscribe(() => {
                    const rawValue = this.form.getRawValue();

                    if (!!rawValue.rolesAssignment) {
                        const rolesControl = this.form.get('rolesAssignment')!;

                        if (rawValue.type === FolderType.Classic) {
                            rolesControl.setValue(RolesAssignment.InheritFromTenant);
                            rolesControl.disable();
                        } else {
                            rolesControl.enable();
                        }
                    }
                });
        }
    }

    ngOnDestroy() {
        this._destroyed$.next();
        this._destroyed$.complete();
    }

    public onSubmit() {
        if (
            !this.form.valid ||
            this.loading$.value
        ) {
            return;
        }

        this._submitPayload(cloneDeep(this.form.getRawValue()));
    }

    private _submitPayload = (payload: any) => {
        this.loading$.next(true);
        if (this.isNewEntry) {
            this._foldersService
                .add(payload)
                .subscribe((folder) => {
                    this._dialogRef.close({
                        type: ModalResultType.Add,
                        data: {
                            current: folder,
                        },
                    });
                }, () => {
                    this.loading$.next(false);
                });

            return;
        }

        payload.id = this.context.data.current!.id;
        const isAppStateFolder = this._appState.user.folder.hasAny &&
            this.context.data.current!.id === this._appState.user.folder.value!.id;

        this._foldersService
            .update(payload.id, payload)
            .pipe(
                switchMap((resp) => {
                    return isAppStateFolder
                        ? this._appState.user.folder.fetch()
                        : of(resp);
                }),
            )
            .subscribe(
                () => {
                    this._dialogRef.close({
                        type: ModalResultType.Update,
                        data: {
                            current: payload,
                        },
                    });
                },
                (error: HttpErrorResponse) => {
                    this.loading$.next(false);
                    if (error.status === 404) {
                        this._dialogRef.close({
                            type: ModalResultType.Failed,
                            additionalData: error,
                        });
                    }
                });
    }
}
